import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  SafeAreaView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../context/AuthContext';
import { rideService } from '../../services/rideService';

const isWeb = Platform.OS === 'web';

const showAlert = (title, message) => {
  if (isWeb) {
    window.alert(`${title}\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

const RequestRideScreen = ({ route, navigation }) => {
  const { pickup, destination, pickupCoords, dropoffCoords, distance } = route.params;
  const [isRequesting, setIsRequesting] = useState(false);
  const [estimatedFare, setEstimatedFare] = useState(null);
  const [fareLoading, setFareLoading] = useState(false);
  const [serviceType, setServiceType] = useState('STANDARD');
  const [pickupEta, setPickupEta] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchFareEstimate();
  }, [serviceType]);

  // Fetch nearby drivers to estimate pickup ETA
  useEffect(() => {
    if (pickupCoords?.latitude && pickupCoords?.longitude) {
      rideService.getNearbyDrivers(pickupCoords.latitude, pickupCoords.longitude)
        .then(res => {
          if (res.success) {
            const count = res.data.drivers?.length || 0;
            // More drivers nearby = shorter wait
            if (count >= 5) setPickupEta('2-4');
            else if (count >= 3) setPickupEta('3-5');
            else if (count >= 1) setPickupEta('5-8');
            else setPickupEta('8-12');
          }
        })
        .catch(() => setPickupEta('3-5'));
    } else {
      setPickupEta('3-5');
    }
  }, []);

  const fetchFareEstimate = async () => {
    if (!pickupCoords || !dropoffCoords) {
      setEstimatedFare(null);
      return;
    }
    setFareLoading(true);
    try {
      const response = await rideService.getFareEstimate(pickupCoords, dropoffCoords, serviceType);
      if (response.success) {
        setEstimatedFare(response.data.estimatedFare);
      }
    } catch (error) {
      console.error('[Obzidn] Fare estimate failed:', error.message);
      setEstimatedFare(null);
    } finally {
      setFareLoading(false);
    }
  };

  const handleRequestRide = async () => {
    setIsRequesting(true);

    try {
      if (!user) {
        showAlert('Error', 'User not authenticated. Please log in again.');
        return;
      }

      // Verify user has the correct role for rider app
      if (user.role === 'DRIVER') {
        showAlert('Wrong Account', 'This is a driver account. Please log out and register a new rider account.');
        return;
      }

      const rideData = {
        origin: pickup,
        destination: destination,
        type: serviceType,
        ...(pickupCoords && { pickupCoords }),
        ...(dropoffCoords && { dropoffCoords }),
      };

      console.log('[Obzidn] Requesting ride with data:', JSON.stringify(rideData), 'user:', user.email, 'role:', user.role);
      const response = await rideService.requestRide(rideData);
      console.log('[Obzidn] Ride request response:', JSON.stringify(response));

      if (response.success) {
        navigation.navigate('TrackDriver', {
          ride: response.data.ride,
        });
      } else {
        showAlert('Error', response.message || 'Failed to request ride');
      }
    } catch (error) {
      const status = error.response?.status;
      console.error('[Obzidn] Request ride error:', status, error.response?.data || error.message);

      let errorMessage = 'Failed to request ride';
      if (status === 401 || status === 403) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showAlert('Error', errorMessage);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleServiceTypeSelect = (type) => {
    setServiceType(type);
    if (type === 'LIMOUSINE') {
      // Navigate to limousine booking screen
      navigation.navigate('BookLimousine', {
        pickup,
        dropoff: destination,
        pickupCoords,
        dropoffCoords,
        distance: distance || 10
      });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        {/* Service Type Selector */}
        <View style={styles.serviceTypeSection}>
          <Text style={styles.sectionTitle}>Service Type</Text>
          <View style={styles.serviceTypeContainer}>
            <TouchableOpacity
              style={[styles.serviceTypeButton, serviceType === 'STANDARD' && styles.serviceTypeButtonActive]}
              onPress={() => handleServiceTypeSelect('STANDARD')}
            >
              <Ionicons 
                name="car" 
                size={24} 
                color={serviceType === 'STANDARD' ? '#fff' : '#666'} 
              />
              <Text style={[styles.serviceTypeText, serviceType === 'STANDARD' && styles.serviceTypeTextActive]}>
                Standard
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.serviceTypeButton, serviceType === 'LIMOUSINE' && styles.serviceTypeButtonActive]}
              onPress={() => handleServiceTypeSelect('LIMOUSINE')}
            >
              <Ionicons 
                name="car-sport" 
                size={24} 
                color={serviceType === 'LIMOUSINE' ? '#fff' : '#666'} 
              />
              <Text style={[styles.serviceTypeText, serviceType === 'LIMOUSINE' && styles.serviceTypeTextActive]}>
                Limousine
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.serviceTypeButton, serviceType === 'CORPORATE' && styles.serviceTypeButtonActive]}
              onPress={() => handleServiceTypeSelect('CORPORATE')}
            >
              <Ionicons 
                name="briefcase" 
                size={24} 
                color={serviceType === 'CORPORATE' ? '#fff' : '#666'} 
              />
              <Text style={[styles.serviceTypeText, serviceType === 'CORPORATE' && styles.serviceTypeTextActive]}>
                Corporate
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Trip Details */}
        <View style={styles.tripDetails}>
          <Text style={styles.sectionTitle}>Trip Details</Text>
          
          <View style={styles.locationRow}>
            <View style={styles.locationIcon}>
              <View style={styles.pickupDot} />
            </View>
            <View style={styles.locationInfo}>
              <Text style={styles.locationLabel}>Pickup</Text>
              <Text style={styles.locationText}>{pickup}</Text>
            </View>
          </View>

          <View style={styles.locationRow}>
            <View style={styles.locationIcon}>
              <View style={styles.destinationDot} />
            </View>
            <View style={styles.locationInfo}>
              <Text style={styles.locationLabel}>Destination</Text>
              <Text style={styles.locationText}>{destination}</Text>
            </View>
          </View>
        </View>

        {/* Vehicle Type */}
        <View style={styles.vehicleSection}>
          <Text style={styles.sectionTitle}>Vehicle</Text>
          <View style={styles.vehicleCard}>
            <Ionicons name="car" size={32} color="#000000" />
            <View style={styles.vehicleInfo}>
              <Text style={styles.vehicleName}>Obzidn Black</Text>
              <Text style={styles.vehicleDescription}>
                {pickupEta ? `Pickup in ~${pickupEta} min` : 'Premium black car service'}
              </Text>
            </View>
            <View style={styles.fareInfo}>
              {fareLoading ? (
                <ActivityIndicator color="#333333" size="small" />
              ) : estimatedFare ? (
                <Text style={styles.fareText}>${estimatedFare}</Text>
              ) : (
                <Text style={styles.fareText}>--</Text>
              )}
              <Text style={styles.fareLabel}>Estimated</Text>
            </View>
          </View>
        </View>

        {/* Payment Method */}
        <View style={styles.paymentSection}>
          <Text style={styles.sectionTitle}>Payment</Text>
          <View style={styles.paymentCard}>
            <Ionicons name="card" size={24} color="#000000" />
            <Text style={styles.paymentText}>Charged after ride</Text>
            <Ionicons name="chevron-forward" size={20} color="#666666" />
          </View>
        </View>
      </View>

      {/* Bottom Button */}
      <View style={styles.bottomSection}>
        <TouchableOpacity
          style={[styles.requestButton, isRequesting && styles.requestButtonDisabled]}
          onPress={handleRequestRide}
          disabled={isRequesting}
        >
          {isRequesting ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator color="#ffffff" size="small" />
              <Text style={styles.requestButtonText}>Requesting...</Text>
            </View>
          ) : (
            <Text style={styles.requestButtonText}>Request Obzidn</Text>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 15,
  },
  serviceTypeSection: {
    marginBottom: 30,
  },
  serviceTypeContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  serviceTypeButton: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E0E0E0',
    backgroundColor: '#F8F8F8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  serviceTypeButtonActive: {
    borderColor: '#000000',
    backgroundColor: '#000000',
  },
  serviceTypeText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
    marginTop: 6,
  },
  serviceTypeTextActive: {
    color: '#fff',
  },
  tripDetails: {
    marginBottom: 30,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  locationIcon: {
    width: 20,
    alignItems: 'center',
    marginRight: 15,
  },
  pickupDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#4CAF50',
  },
  destinationDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#F44336',
  },
  locationInfo: {
    flex: 1,
  },
  locationLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 2,
  },
  locationText: {
    fontSize: 16,
    color: '#333333',
  },
  vehicleSection: {
    marginBottom: 30,
  },
  vehicleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    padding: 20,
    borderWidth: 2,
    borderColor: '#000000',
  },
  vehicleInfo: {
    flex: 1,
    marginLeft: 15,
  },
  vehicleName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 2,
  },
  vehicleDescription: {
    fontSize: 14,
    color: '#666666',
  },
  fareInfo: {
    alignItems: 'flex-end',
  },
  fareText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333333',
  },
  fareLabel: {
    fontSize: 12,
    color: '#666666',
  },
  paymentSection: {
    marginBottom: 30,
  },
  paymentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    padding: 20,
  },
  paymentText: {
    flex: 1,
    fontSize: 16,
    color: '#333333',
    marginLeft: 15,
  },
  bottomSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  requestButton: {
    backgroundColor: '#000000',
    borderRadius: 12,
    paddingVertical: 18,
    alignItems: 'center',
  },
  requestButtonDisabled: {
    opacity: 0.6,
  },
  requestButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default RequestRideScreen;
